import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Card, SectionTitle } from '../../shared/components';
import { StateChip } from '../components/Chips';
import TextPromptDialog from '../components/TextPromptDialog';
import TimeEntryDialog from '../components/TimeEntryDialog';
import { useTaskMeta } from '../components/useTaskMeta';
import { myWorkApi, timesheetApi } from '../tasks.api';
import type { Timesheet } from '../tasks.types';
import { errorMessage, fmtDate, fmtDateTime, fmtHours, humanize, isoDate, num, today, weekStart } from '../tasks.utils';

/** One week as a task × day grid, with its entries. Time outside a task gets a row per category. */
function WeekGrid({ sheet, onDelete }: { sheet: Timesheet; onDelete?: (entryId: number) => void }) {
  return (
    <Stack gap={2}>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>Task / work</TableCell>
            {sheet.dates.map((d) => (
              <TableCell key={d} align='right' sx={{ bgcolor: d === today() ? 'action.hover' : undefined }}>
                {dayjs(d).format('ddd DD')}
              </TableCell>
            ))}
            <TableCell align='right'>Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sheet.rows.map((r) => (
            <TableRow key={r.taskId ?? `category-${r.categoryId}`}>
              <TableCell>
                <Typography variant='body2' fontWeight={600} color={r.taskId == null ? 'text.secondary' : undefined}>
                  {r.taskId == null ? 'Other work' : r.taskCode}
                </Typography>
                <Typography variant='caption'>{r.taskTitle}</Typography>
              </TableCell>
              {r.hours.map((h, i) => (
                <TableCell key={i} align='right'>{num(h) ? fmtHours(h) : ''}</TableCell>
              ))}
              <TableCell align='right'><b>{fmtHours(r.total)}</b></TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell><b>Logged</b></TableCell>
            {sheet.dayTotals.map((h, i) => <TableCell key={i} align='right'><b>{fmtHours(h)}</b></TableCell>)}
            <TableCell align='right'><b>{fmtHours(sheet.loggedHours)}</b></TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ color: 'text.secondary' }}>Capacity</TableCell>
            {sheet.capacity.map((h, i) => <TableCell key={i} align='right' sx={{ color: 'text.secondary' }}>{fmtHours(h)}</TableCell>)}
            <TableCell align='right' sx={{ color: 'text.secondary' }}>{fmtHours(sheet.capacityHours)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {sheet.entries.length > 0 && (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Task / work</TableCell>
              <TableCell>From – to</TableCell>
              <TableCell align='right'>Hours</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Notes</TableCell>
              {onDelete && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {sheet.entries.map((e) => (
              <TableRow key={e.id}>
                <TableCell>{fmtDate(e.workDate)}</TableCell>
                <TableCell>{e.taskCode ?? e.categoryName}</TableCell>
                <TableCell>{e.startTime ? dayjs(e.startTime).format('HH:mm') : '—'} – {e.running ? 'running' : e.endTime ? dayjs(e.endTime).format('HH:mm') : '—'}</TableCell>
                <TableCell align='right'>{fmtHours(e.hours)}</TableCell>
                <TableCell>{humanize(e.entryType)}</TableCell>
                <TableCell>{e.notes}</TableCell>
                {onDelete && (
                  <TableCell align='right'>
                    {!e.running && (
                      <IconButton size='small' aria-label='Delete entry' onClick={() => onDelete(e.id)}>
                        <Icon icon='mdi:delete-outline' />
                      </IconButton>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Stack>
  );
}

/**
 * Two menu entries share this page (V30): My Workspace → My Timesheet (`mine`, the caller's
 * week) and Tasks → Team Timesheets (`team`, the weeks waiting for the caller's review).
 */
export default function TimesheetPage({ mode }: { mode: 'mine' | 'team' }) {
  const meta = useTaskMeta();
  const tab = mode === 'mine' ? 0 : 1;
  const [monday, setMonday] = useState(weekStart(today()));
  const [sheet, setSheet] = useState<Timesheet | null>(null);
  const [loggable, setLoggable] = useState<{ id: number; label: string }[]>([]);
  const [queue, setQueue] = useState<Timesheet[]>([]);
  const [open, setOpen] = useState<number | null>(null);
  const [logging, setLogging] = useState(false);
  const [loggingOther, setLoggingOther] = useState(false);
  const [rejecting, setRejecting] = useState<Timesheet | null>(null);
  const [loading, setLoading] = useState(false);
  const canReview = Boolean(meta?.permissions.TIMESHEET_APPROVE);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, w] = await Promise.all([timesheetApi.weekly(monday), myWorkApi.week(monday)]);
      setSheet(s);
      setLoggable(
        w.tasks.filter((t) => t.assignmentStatus === 'ACTIVE').map((t) => ({ id: t.task.id, label: `${t.task.taskCode} · ${t.task.title}` }))
      );
    } catch (e) {
      toast.error(errorMessage(e, 'Failed to load the timesheet'));
    } finally {
      setLoading(false);
    }
  }, [monday]);

  const loadQueue = useCallback(async () => {
    try {
      setQueue(await timesheetApi.review());
    } catch (e) {
      toast.error(errorMessage(e, 'Failed to load the review queue'));
    }
  }, []);

  useEffect(() => {
    if (tab === 0) load();
    else loadQueue();
  }, [tab, load, loadQueue]);

  const act = async (fn: () => Promise<unknown>, ok: string, after: () => void) => {
    try {
      await fn();
      toast.success(ok);
      after();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <Stack gap={3}>
      <SectionTitle title={mode === 'mine' ? 'MY TIMESHEET' : `TEAM TIMESHEETS${queue.length ? ` (${queue.length})` : ''}`} />
      {mode === 'team' && meta && !canReview && (
        <Alert severity='info'>Reviewing timesheets is for managers; your login only sees its own week.</Alert>
      )}

      {tab === 0 && (
        <Card sx={{ py: 2 }}>
          <Stack direction='row' alignItems='center' gap={1} flexWrap='wrap'>
            <IconButton aria-label='Previous week' onClick={() => setMonday(isoDate(dayjs(monday).subtract(1, 'week')))}><Icon icon='mdi:chevron-left' /></IconButton>
            <Typography fontWeight={600}>
              Week {sheet?.isoWeek} · {fmtDate(monday)} – {fmtDate(isoDate(dayjs(monday).add(6, 'day')))}
            </Typography>
            <IconButton aria-label='Next week' onClick={() => setMonday(isoDate(dayjs(monday).add(1, 'week')))}><Icon icon='mdi:chevron-right' /></IconButton>
            <Button size='small' onClick={() => setMonday(weekStart(today()))}>This week</Button>
            {sheet && <StateChip status={sheet.status} label={sheet.statusLabel} />}
            <Stack direction='row' gap={1} sx={{ ml: 'auto' }}>
              {sheet?.editable && (
                <>
                  <Button variant='outlined' startIcon={<Icon icon='mdi:clock-plus-outline' />} onClick={() => setLogging(true)}>Log time</Button>
                  <Button variant='outlined' startIcon={<Icon icon='mdi:account-group-outline' />} onClick={() => setLoggingOther(true)}>Other work</Button>
                  <Button variant='contained' disabled={monday > today()} onClick={() => act(() => timesheetApi.submit(monday), 'Timesheet submitted', load)}>Submit week</Button>
                </>
              )}
            </Stack>
          </Stack>
          {loading && <LinearProgress />}
          {sheet?.status === 'REJECTED' && <Alert severity='warning'>Returned by {sheet.reviewedBy?.name}: {sheet.reviewRemarks}</Alert>}
          {sheet?.status === 'APPROVED' && <Alert severity='success'>Approved by {sheet.reviewedBy?.name} on {fmtDateTime(sheet.reviewedAt)}.</Alert>}
          {sheet?.status === 'SUBMITTED' && <Alert severity='info'>Submitted {fmtDateTime(sheet.submittedAt)}; the week is locked while it is reviewed.</Alert>}
          {sheet && (
            <WeekGrid sheet={sheet} onDelete={sheet.editable ? (id) => act(() => timesheetApi.removeEntry(id), 'Entry deleted', load) : undefined} />
          )}
        </Card>
      )}

      {tab === 1 && (
        <Stack gap={2}>
          {queue.length === 0 && <Alert severity='success'>No timesheets waiting for review.</Alert>}
          {queue.map((s) => (
            <Card key={s.id} sx={{ py: 2 }}>
              <Stack direction='row' alignItems='center' gap={2} flexWrap='wrap'>
                <Typography fontWeight={600}>{s.employeeName}</Typography>
                <Typography>Week {s.isoWeek} · {fmtDate(s.weekStart)}</Typography>
                <Typography color='text.secondary'>{fmtHours(s.loggedHours)} logged of {fmtHours(s.capacityHours)}</Typography>
                <Stack direction='row' gap={1} sx={{ ml: 'auto' }}>
                  <Button size='small' onClick={() => setOpen(open === s.id ? null : s.id)}>{open === s.id ? 'Hide' : 'Details'}</Button>
                  <Button size='small' color='error' onClick={() => setRejecting(s)}>Return</Button>
                  <Button size='small' variant='contained' color='success' onClick={() => act(() => timesheetApi.approve(s.id as number), 'Approved', loadQueue)}>Approve</Button>
                </Stack>
              </Stack>
              {open === s.id && <WeekGrid sheet={s} />}
            </Card>
          ))}
        </Stack>
      )}

      <TimeEntryDialog open={loggingOther} tasks={[]} target='other' date={monday > today() ? today() : dayjs(monday).add(6, 'day').isAfter(dayjs()) ? today() : monday}
        onClose={() => setLoggingOther(false)} onDone={() => { setLoggingOther(false); load(); }} />
      <TimeEntryDialog open={logging} tasks={loggable} date={monday > today() ? today() : dayjs(monday).add(6, 'day').isAfter(dayjs()) ? today() : monday}
        onClose={() => setLogging(false)} onDone={() => { setLogging(false); load(); }} />
      {rejecting && (
        <TextPromptDialog open title='RETURN TIMESHEET' label='What needs correcting?' required submitColor='error' submitLabel='Return'
          onClose={() => setRejecting(null)}
          onSubmit={async (text) => { await act(() => timesheetApi.reject(rejecting.id as number, text), 'Returned to the employee', loadQueue); setRejecting(null); }} />
      )}
    </Stack>
  );
}
